import Logout16 from '@carbon/icons-react/es/logout/16';
import Time16 from '@carbon/icons-react/es/time/16';
import { List, Tooltip } from 'antd';
import React, { FC, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { User, UserStatus } from 'api/models';
import { Project } from 'api/project';
import { Avatar } from 'components/atoms/avatar';
import { Button } from 'components/atoms/button';
import { ProjectMember } from 'components/molecules/projects/project-member';
import { BodyShort01, Caption01 } from 'components/styles';
import { removeMembersFromProject } from 'data/projects/actions';
import { useUser } from 'hooks/use-user';
import { Membership } from 'stores/memberships';
import { ProjectPermissionSetType } from 'stores/permission-sets';
import { getUserLabel } from 'utils/get-user-label';

const Text = styled.p`
  ${BodyShort01};
  margin-bottom: 0;
`;

const PendingStatusText = styled(Button).attrs({
  readOnly: true,
  type: 'text',
})`
  color: var(--orange-40);
  cursor: default;

  &&:not([disabled]):hover {
    background-color: transparent;
  }
`;

const MembeDetails = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 0;
  justify-content: center;
  height: var(--spacing-07);

  & p {
    margin-bottom: 0;
  }
`;

const MemberList = styled(List.Item)`
  padding: var(--spacing-03) var(--spacing-05);

  .ant-list-item-action li {
    padding-right: 0;
  }
`;

const MemberRole = styled(Text)`
  ${Caption01};
  color: var(--gray-70);
`;

export const ListItem: FC<{
  member: User;
  loggedInUserPermissions: Membership | undefined;
  project: Project;
}> = ({ member, loggedInUserPermissions, project }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const dispatch = useDispatch();
  const loggedInUser = useUser();

  const { projectId, projectOwner } = project;
  const handleSuccess = useCallback(() => {
    setIsLoading(false);
  }, []);

  const handleFailure = useCallback(() => {
    setIsLoading(false);
  }, []);

  const handleRemove = (user: User, _projectId: number) => {
    setIsLoading(true);
    dispatch(
      removeMembersFromProject({
        projectId: _projectId,
        members: [{ userId: user.userId }],
        handleSuccess,
        handleFailure,
      }),
    );
  };

  if (member) {
    return (
      <MemberList
        actions={[
          <>
            {loggedInUserPermissions?.memberType !==
              ProjectPermissionSetType.Owner &&
            member.userId === loggedInUser.user.userId ? (
              <Tooltip title={<FormattedMessage id="leave_project" />}>
                <Button
                  type="text"
                  danger
                  loading={isLoading}
                  icon={<Logout16 />}
                  onClick={() => handleRemove(member, projectId)}
                  disabled={projectOwner.userId === loggedInUser.user.userId}
                />
              </Tooltip>
            ) : (
              member.userStatus === UserStatus.Invited && (
                <Tooltip title={<FormattedMessage id="invitation.pending" />}>
                  <PendingStatusText icon={<Time16 />} />
                </Tooltip>
              )
            )}
          </>,
        ]}
      >
        <List.Item.Meta
          avatar={
            <Avatar
              size="large"
              src={member.photoUrl}
              userStatus={member.userStatus}
              name={getUserLabel(member)}
            />
          }
          title={
            <MembeDetails>
              <ProjectMember member={member} projectId={projectId} />
              {member?.role && <MemberRole>{member.role.roleName}</MemberRole>}
            </MembeDetails>
          }
          description={member.role && member.role.roleDescription}
        />
      </MemberList>
    );
  }

  return <></>;
};
